import React, { useEffect, useState } from 'react';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { API } from '@src/api';
import { getTokenFromStorage, saveTokenToStorage } from '@src/utills/storage';

export type TimeoutModalProps = {
  token: string;
};

// This is what our token looks like.
export type TokenPayload = {
  email: string;
  exp: number; // This is the field we care about.
  iss: string; // Should always be "scrf".
  first_name: string;
  last_name: string;
  roles: string[];
  sub: string; // Advocate's MSID
};

const THIRTY_SECONDS_IN_MILLIS: number = 30_000;
const FIVE_MINUTES_IN_SECONDS: number = 300;

/**
 * Given a JSON web token as a string, return the token's payload
 * as a plain ol' javascript object.
 * NOTE - This is insecure, since the signature of the token is not validated.
 *  Do not use this function for anything related to security or verification.
 *
 * @param token the JWT, as a string
 * @returns the payload of the token as a `TokenPayload` object, or
 *  undefined if the token is malformed in some fashion.
 */
export function decodePayload(token: string): TokenPayload | undefined {
  try {
    const jwtParts = token.split('.');
    const base64Payload = jwtParts[1];
    if (!base64Payload) {
      return undefined;
    }
    const payload = Buffer.from(base64Payload, 'base64').toString('utf-8');
    const decodedPayload = JSON.parse(payload) as TokenPayload;
    return decodedPayload;
  } catch (e) {
    console.error("Couldn't decode token: ", e);
  }

  return undefined;
}

function checkTokenExpiringSoon(tokenExpTimestamp: number): boolean {
  const currentUnixTimestamp = Math.floor(new Date().getTime() / 1000);
  // Does this token expire in less than 5 minutes?
  return tokenExpTimestamp < currentUnixTimestamp + FIVE_MINUTES_IN_SECONDS;
}

/**
 * Get a new token for the user, and put it in storage.
 */
async function refreshToken() {
  const resp = await API.refreshToken();

  if (resp?.status === 'success' && !!resp?.data?.token) {
    saveTokenToStorage(resp.data.token);
    return;
  }

  return new Error('Could not refresh token');
}

export function TimeoutModal() {
  const { navigate } = useRouter();

  // 2min
  const checkTokenInterval = THIRTY_SECONDS_IN_MILLIS * 4;

  // Track whether the modal is open
  const [showModal, setShowModal] = useState<boolean>(false);

  // Show the user a loading spinner while the request to restart the session
  // is in progress.
  const [_showLoadingIndicator, setShowLoadingIndicator] =
    useState<boolean>(false);

  /**
   * Check the decoded token payload every 120 seconds, showing the modal
   * if the token expires in under 5 minutes
   */
  function checkTokenForExpiration(): void {
    const token = getTokenFromStorage();
    const decodedToken = decodePayload(token);
    const exp = decodedToken?.exp ?? Infinity;
    const expiringSoon = checkTokenExpiringSoon(exp);
    setShowModal(expiringSoon);
    setTimeout(checkTokenForExpiration, checkTokenInterval);
  }

  useEffect(() => {
    checkTokenForExpiration();
  }, []);

  return (
    <React.Fragment>
      <ModalDialog
        header="Your session will expire soon"
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        data-cy="session-expiry-modal"
      >
        <div data-cy="session-expiry-modal-content">
          <div>
            <Text>
              Your login session will expire soon. If you don't refresh your
              session, you might be interrupted and signed out.
            </Text>
          </div>

          <div>
            <Layout.Group alignLayout="center">
              <Button
                data-cy="refresh-session-button"
                onClick={async () => {
                  setShowLoadingIndicator(true);
                  const refreshed: void | Error = await refreshToken();
                  if (refreshed instanceof Error) {
                    setShowLoadingIndicator(false);
                    console.error(refreshed.message);
                    return;
                  }

                  setShowLoadingIndicator(false);
                  setShowModal(false);
                }}
              >
                Refresh my session
              </Button>

              <Button
                data-cy="refresh-modal-logout-button"
                variant="outline"
                onClick={() => {
                  navigate('/logout');
                }}
              >
                Log out
              </Button>
            </Layout.Group>
          </div>
        </div>
      </ModalDialog>
    </React.Fragment>
  );
}
